/**
 * SideBySideOpportunityView — Day 5 PoC layout.
 *
 *   LEFT COLUMN  → existing TrustCard (free Prime Pillar view, unchanged)
 *   RIGHT COLUMN → Arbitrage Pro view ($199/mo gated)
 *                  • CountryMatchPanel (visible to FREE as basic teaser)
 *                  • RevenueBenchmarkPanel (Pro only — gated)
 *                  • ArbitrageLLMPanel    (Pro only — gated)
 *
 * Cached data only — no LLM calls on view. Page-load target <2s.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import TrustCard from "../components/TrustCard";
import CountryMatchPanel from "../components/CountryMatchPanel";
import RevenueBenchmarkPanel from "../components/RevenueBenchmarkPanel";
import ArbitrageLLMPanel from "../components/ArbitrageLLMPanel";
import ProTierGate from "../components/ProTierGate";
import AlgorithmConfidenceScore from "../components/AlgorithmConfidenceScore";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FounderGutCheck = () => (
  <div
    data-testid="founder-gut-check"
    className="mt-4 p-3 border-l-4 border-[#7C3AED] bg-[#7C3AED]/5 text-xs"
  >
    <div className="font-black uppercase tracking-wider text-[#7C3AED] mb-1">
      Founder gut-check
    </div>
    <p className="text-zinc-700 leading-snug">
      Looking at the data on the right vs the left — does the Pro view make you reach for your credit card? Why or why not?
    </p>
  </div>
);

const SideBySideOpportunityView = ({ opportunity, onUpgradeClick }) => {
  const [data, setData] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadMs, setLoadMs] = useState(null);

  // Fetch global confidence score once on mount (cheap aggregate, no per-opp dependency)
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE}/arbitrage/confidence-score`, { timeout: 5000 })
      .then((res) => {
        if (!cancelled) setConfidence(res.data);
      })
      .catch(() => {
        /* non-fatal — banner just hides */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const oppId = opportunity?.id;
    if (!oppId) return undefined;
    let cancelled = false;
    const startedAt = performance.now();
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios
      .get(`${API_BASE}/opportunities/${oppId}/arbitrage`, { headers, timeout: 8000 })
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setError(null);
        setLoadMs(Math.round(performance.now() - startedAt));
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e.message);
        setLoadMs(Math.round(performance.now() - startedAt));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [opportunity?.id]);

  const isPro = Boolean(data?.is_pro);
  const algoTop = data?.country_recommendations?.[0]?.country_code
    || data?.teaser?.top_country?.country_code;

  return (
    <div data-testid="side-by-side-view" className="space-y-3">
      {confidence && confidence.total_opps > 0 && (
        <AlgorithmConfidenceScore
          agreementRate={confidence.agreement_rate}
          totalOpps={confidence.total_opps}
          agreedCount={confidence.agreed_count}
        />
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT — free Prime Pillar view (unchanged) */}
        <div data-testid="free-view-column" className="space-y-2">
          <div className="flex items-center gap-2 pb-2 border-b border-zinc-200">
            <span className="text-xs font-black uppercase tracking-wider text-zinc-500">Quick view</span>
            <span className="text-[10px] font-mono text-zinc-400 px-1.5 py-0.5 bg-zinc-100">the basics</span>
          </div>
          <TrustCard opportunityId={opportunity.id} />
        </div>

        {/* RIGHT — Arbitrage Pro view */}
        <div data-testid="pro-view-column" className="space-y-3">
          <div className="flex items-center justify-between pb-2 border-b border-zinc-200">
            <div className="flex items-center gap-2">
              <span className="text-xs font-black uppercase tracking-wider text-[#7C3AED]">
                Country-fit analysis
              </span>
              <span className="text-[10px] font-mono text-emerald-700 px-1.5 py-0.5 bg-emerald-50">
                free
              </span>
            </div>
            {loadMs !== null && (
              <span
                className="text-[10px] font-mono text-zinc-400"
                title="Time to fetch cached arbitrage payload (no LLM)"
                data-testid="page-load-time"
              >
                load {loadMs}ms
              </span>
            )}
          </div>

          {loading && (
            <div data-testid="arbitrage-loading" className="p-4 border border-zinc-200 bg-zinc-50 text-sm text-zinc-500">
              Loading cached Arbitrage Pro analysis…
            </div>
          )}

          {error && (
            <div data-testid="arbitrage-error" className="p-3 border border-red-200 bg-red-50 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && data && !data.available && (
            <div data-testid="arbitrage-unavailable" className="p-4 border border-amber-200 bg-amber-50 text-sm text-amber-800">
              Arbitrage analysis not yet generated for this opportunity. It will appear automatically after the next nightly run.
            </div>
          )}

          {!loading && !error && data && data.available && (
            isPro ? (
              // ----- PRO TIER FULL PANELS -----
              <>
                <CountryMatchPanel
                  countries={data.country_recommendations || []}
                  computedAt={data.computed_at}
                />
                <RevenueBenchmarkPanel
                  matches={data.matches || []}
                  alignment={data.alignment}
                  topCountry={algoTop}
                />
                <ArbitrageLLMPanel
                  analysis={data.analysis}
                  algoTopCountry={algoTop}
                  modelsUsed={data.models_used}
                />
              </>
            ) : (
              // ----- FREE TIER TEASER + 2 GATED PANELS -----
              <>
                {/* Teaser: top country only, visible to free */}
                {data.teaser?.top_country && (
                  <CountryMatchPanel
                    countries={[data.teaser.top_country]}
                    computedAt={null}
                  />
                )}
                <div data-testid="free-tier-teaser-stats" className="p-3 border border-zinc-200 bg-zinc-50 text-xs space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-600">Revenue comps found</span>
                    <span className="font-mono font-bold">{data.teaser?.match_count ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-600">Loopholes identified</span>
                    <span className="font-mono font-bold">{data.teaser?.loophole_count ?? 0}</span>
                  </div>
                  {data.teaser?.has_alternative_country && (
                    <div className="flex items-center justify-between text-amber-700">
                      <span>Opus 4.8 wildcard alternative</span>
                      <span className="font-mono font-bold">{data.teaser?.alternative_country_code || "—"}</span>
                    </div>
                  )}
                </div>

                <ProTierGate
                  headline="Revenue Benchmark · TrustMRR Comps"
                  subhead={`See all ${data.teaser?.match_count ?? 0} verified comparable startups with MRR, growth, and country alignment.`}
                  features={(data.upgrade_cta?.features || []).slice(0, 3)}
                  onUpgrade={onUpgradeClick}
                  priceUsd={data.upgrade_cta?.price_usd_monthly || 199}
                >
                  <RevenueBenchmarkPanel
                    matches={Array.from({ length: data.teaser?.match_count ?? 3 }).map((_, i) => ({
                      trustmrr_id: `placeholder-${i}`,
                      name: "███████ ███████",
                      country: "US",
                      category: "███████",
                      revenue_mrr_usd: 12000 + i * 5000,
                      growth_30d: 10,
                      match_score: 0.78,
                      reasoning: "█████████ █████████ █████████ █████████ ████████.",
                    }))}
                    alignment={null}
                    topCountry={algoTop}
                  />
                </ProTierGate>

                <ProTierGate
                  headline="Arbitrage LLM Analysis"
                  subhead={
                    data.teaser?.has_alternative_country
                      ? `Opus 4.8 thinks ${data.teaser.alternative_country_code} might work BETTER than ${algoTop} &mdash; full reasoning + ${data.teaser.loophole_count} verbatim-quoted loopholes await.`
                      : "Get Opus 4.8's full structured analysis with loopholes and MVP spec."
                  }
                  features={(data.upgrade_cta?.features || []).slice(0, 3)}
                  onUpgrade={onUpgradeClick}
                  priceUsd={data.upgrade_cta?.price_usd_monthly || 199}
                >
                  <ArbitrageLLMPanel
                    analysis={{
                      loopholes: Array.from({ length: data.teaser?.loophole_count ?? 3 }).map((_, i) => ({
                        gap: "███████ ███████ ███████ ███████ ███████ ███████.",
                        evidence_quote: "█████████ █████████ █████████ █████████ █████████ █████████.",
                        evidence_source: "g2",
                        exploitation_strategy: "█████████ █████████ █████████ █████████.",
                      })),
                      country_arbitrage: {
                        primary_recommendation: algoTop,
                        primary_reasoning: "█████████ █████████ █████████ █████████ █████████.",
                        alternative_recommendation: data.teaser?.alternative_country_code,
                        alternative_reasoning: "█████████ █████████ █████████ █████████ █████████.",
                      },
                      adaptation_spec: {
                        target_country: data.teaser?.alternative_country_code || algoTop,
                        confidence: "medium",
                        tech_stack: { frontend: "███████", backend: "███████", db: "███████" },
                        launch_budget_usd: 5000,
                      },
                      confidence_notes: "███████ ███████ ███████ ███████.",
                    }}
                    algoTopCountry={algoTop}
                  />
                </ProTierGate>
              </>
            )
          )}
        </div>
      </div>
      <FounderGutCheck />
    </div>
  );
};

export default SideBySideOpportunityView;
